import { useState, useEffect } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { getAlertsConfig, saveAlertsConfig } from "@/lib/delivery";

const RULES = [
  {
    key: "delivery_rate_7d",
    label: "Rată livrare 7 zile sub prag",
    description: "Alertă critică când rata de livrare pe ultimele 7 zile scade sub pragul setat.",
    fields: [{ path: "threshold_pct", label: "Prag %", step: 0.5 }],
  },
  {
    key: "county_refusal_14d",
    label: "Județ cu rată refuz peste prag",
    description: "Un județ depășește pragul de refuz pe 14 zile, cu volum minim de colete.",
    fields: [
      { path: "threshold_pct", label: "Prag % refuz", step: 0.5 },
      { path: "min_shipments", label: "Minim colete finalizate", step: 1 },
    ],
  },
  {
    key: "stuck_shipment",
    label: "Colet blocat în tranzit",
    description: "Alertă când un colet nu are eveniment nou de N zile.",
    fields: [{ path: "days", label: "Zile fără eveniment", step: 1 }],
  },
  {
    key: "damaged_weekly",
    label: "Prea multe colete avariate",
    description: "Alertă la un număr mare de colete avariate într-o săptămână.",
    fields: [{ path: "max_per_week", label: "Max /săptămână", step: 1 }],
  },
];

export default function AlertsConfigDialog({ open, onOpenChange, onSaved }) {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    getAlertsConfig()
      .then(setCfg)
      .catch((e) => toast.error("Eroare: " + e.message))
      .finally(() => setLoading(false));
  }, [open]);

  const upd = (rule, path, val) => {
    setCfg((c) => ({ ...c, [rule]: { ...c[rule], [path]: val } }));
  };

  const save = async () => {
    setSaving(true);
    try {
      await saveAlertsConfig(cfg);
      toast.success("Setări alerte salvate. Se vor aplica la următorul sync GLS.");
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Configurare alerte livrări</DialogTitle>
        </DialogHeader>
        {loading || !cfg ? (
          <div className="py-10 flex items-center justify-center text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin mr-2" /> Se încarcă...
          </div>
        ) : (
          <div className="space-y-4 text-sm">
            {RULES.map((rule) => {
              const c = cfg[rule.key] || {};
              return (
                <div
                  key={rule.key}
                  className="rounded-lg border border-border p-3"
                  data-testid={`alert-rule-${rule.key}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <div className="font-medium">{rule.label}</div>
                      <div className="text-xs text-muted-foreground mt-0.5">{rule.description}</div>
                    </div>
                    <Switch
                      checked={!!c.enabled}
                      onCheckedChange={(v) => upd(rule.key, "enabled", v)}
                      data-testid={`alert-toggle-${rule.key}`}
                    />
                  </div>
                  {c.enabled && (
                    <div className="grid grid-cols-2 gap-3 mt-3">
                      {rule.fields.map((f) => (
                        <div key={f.path} className="space-y-1">
                          <Label className="text-xs text-muted-foreground">{f.label}</Label>
                          <Input
                            type="number"
                            step={f.step}
                            value={c[f.path] ?? 0}
                            onChange={(e) => upd(rule.key, f.path, parseFloat(e.target.value) || 0)}
                            className="h-8"
                            data-testid={`alert-input-${rule.key}-${f.path}`}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Anulează</Button>
          <Button onClick={save} disabled={saving || !cfg} data-testid="save-alerts-config-btn">
            {saving && <Loader2 className="h-4 w-4 animate-spin mr-1.5" />}
            Salvează
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
